import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../firebase/config";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";

// Ícones
import { IoLocationOutline, IoSearchSharp } from "react-icons/io5";
import { CgProfile } from "react-icons/cg";
import { CiBoxes } from "react-icons/ci";
import { CiCreditCard1 } from "react-icons/ci";
import { LiaToolsSolid } from "react-icons/lia";
import { FiUploadCloud } from "react-icons/fi";

import "./Dashboard.css";
import toast from "react-hot-toast";

const CATEGORIAS_BASE = ["esporas", "bombas", "cuias"];

export function Dashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Estados de Navegação e Pedidos
  const [abaAtiva, setAbaAtiva] = useState("pedidos");
  const [pedidos, setPedidos] = useState([]);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
  const [filtroPesquisa, setFiltroPesquisa] = useState("");
  const [filtroData, setFiltroData] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  // Mapa de tradução de status
  const statusMap = {
    pending: "Pendente",
    approved: "Aprovado",
    shipped: "Enviado",
    delivered: "Entregue",
    cancelled: "Cancelado",
  };

  const getStatusEmPortugues = (statusIngles) =>
    statusMap[statusIngles] || statusIngles;

  // Estados para Modal de Envio Personalizado
  const [modalEnvioAberta, setModalEnvioAberta] = useState(false);
  const [pedidoParaEnviar, setPedidoParaEnviar] = useState(null);
  const [tipoEnvio, setTipoEnvio] = useState("pessoal");
  const [codigoRastreio, setCodigoRastreio] = useState("");

  const [informacoes, setInformacoes] = useState([]);
  const [estoque, setEstoque] = useState("");

  // Estados de Produtos
  const [nome, setNome] = useState("");
  const [preco, setPreco] = useState("");
  const [categoria, setCategoria] = useState("esporas");
  const [novaCategoria, setNovaCategoria] = useState("");
  const [descricao, setDescricao] = useState("");
  const [produtos, setProdutos] = useState([]);
  const [editandoId, setEditandoId] = useState(null);
  const [produtoParaExcluir, setProdutoParaExcluir] = useState(null);
  const [modalDescontoAberta, setModalDescontoAberta] = useState(false);
  const [modalRemoverDescontoAberta, setModalRemoverDescontoAberta] =
    useState(false);
  const [tipoDesconto, setTipoDesconto] = useState("percentual");
  const [valorDesconto, setValorDesconto] = useState("");
  const [categoriaDesconto, setCategoriaDesconto] = useState("");
  const [produtosSelecionadosDesconto, setProdutosSelecionadosDesconto] =
    useState([]);
  const [
    produtosSelecionadosRemocaoDesconto,
    setProdutosSelecionadosRemocaoDesconto,
  ] = useState([]);

  // Estados de Upload de Imagem (ImgBB)
  const [fotos, setFotos] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const MAX_FOTOS = 5;

  const categoriasDisponiveis = useMemo(() => {
    const categoriasDoBanco = produtos
      .map((produto) => produto.categoria)
      .filter(Boolean);

    return Array.from(new Set([...CATEGORIAS_BASE, ...categoriasDoBanco]));
  }, [produtos]);

  const categoriasParaDesconto = useMemo(() => {
    const categoriasDoBanco = produtos
      .map((produto) => produto.categoria)
      .filter(Boolean);

    return Array.from(new Set(categoriasDoBanco));
  }, [produtos]);

  const normalizarProdutoComPrecoBase = (produto) => {
    const precoAtual = Number(produto?.preco ?? 0);
    const precoOriginal =
      produto?.precoOriginal !== undefined && produto?.precoOriginal !== null
        ? Number(produto.precoOriginal)
        : precoAtual;

    return {
      ...produto,
      preco: precoAtual,
      precoOriginal,
    };
  };

  const produtosFiltradosParaDesconto = useMemo(() => {
    const produtosBase = categoriaDesconto
      ? produtos.filter((produto) => produto.categoria === categoriaDesconto)
      : produtos;

    return produtosBase.sort((a, b) => a.nome.localeCompare(b.nome));
  }, [categoriaDesconto, produtos]);

  const todosProdutosVisiveisSelecionados =
    produtosFiltradosParaDesconto.length > 0 &&
    produtosFiltradosParaDesconto.every((produto) =>
      produtosSelecionadosDesconto.includes(produto.id),
    );

  const produtosComDesconto = useMemo(
    () =>
      produtos.filter(
        (produto) =>
          Number(produto.precoOriginal ?? produto.preco ?? 0) >
          Number(produto.preco ?? 0),
      ),
    [produtos],
  );


  const API_KEY_IMGBB = process.env.REACT_APP_IMGBB_API_KEY;

  const carregarProdutos = async () => {
    const data = await getDocs(collection(db, "produtos"));
    setProdutos(
      data.docs.map((doc) =>
        normalizarProdutoComPrecoBase({ ...doc.data(), id: doc.id }),
      ),
    );
  };

  const adicionarInformacao = () => {
    setInformacoes([...informacoes, { chave: "", valor: "" }]);
  };

  const removerInformacao = (index) => {
    const novasInfos = informacoes.filter((_, i) => i !== index);
    setInformacoes(novasInfos);
  };

  const handleInfoChange = (index, campo, valor) => {
    const novasInfos = [...informacoes];
    novasInfos[index][campo] = valor;
    setInformacoes(novasInfos);
  };

  useEffect(() => {
    const unsubscribeProdutos = onSnapshot(
      collection(db, "produtos"),
      (snapshot) => {
        setProdutos(
          snapshot.docs.map((doc) =>
            normalizarProdutoComPrecoBase({ ...doc.data(), id: doc.id }),
          ),
        );
      },
    );

    const q = query(collection(db, "pedidos"), orderBy("dataCriacao", "desc"));
    const unsubscribePedidos = onSnapshot(q, (snapshot) => {
      setPedidos(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    });

    return () => {
      unsubscribeProdutos();
      unsubscribePedidos();
    };
  }, []);

  const uploadParaImgBB = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    const response = await fetch(
      `https://api.imgbb.com/1/upload?key=${API_KEY_IMGBB}`,
      {
        method: "POST",
        body: formData,
      },
    );
    const result = await response.json();
    if (result.success) return result.data.url;
    throw new Error("Erro no upload da imagem");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setCarregando(true);
    try {
      if (previewUrls.length === 0) {
        toast.error("Anexe pelo menos uma imagem para o produto.");
        setCarregando(false);
        return;
      }

      // Upload de fotos novas e manter URLs existentes
      const fotosFinais = [];
      for (let i = 0; i < previewUrls.length; i++) {
        const preview = previewUrls[i];
        // Se começa com blob, é nova; senão, é URL já existente
        if (preview.startsWith("blob:")) {
          const fotoUpload = await uploadParaImgBB(fotos[i]);
          fotosFinais.push(fotoUpload);
        } else {
          fotosFinais.push(preview);
        }
      }

      const categoriaFinal =
        categoria === "outros"
          ? novaCategoria.trim().toLowerCase()
          : categoria.toLowerCase();

      const categoriaExistente = produtos.find(
        (produto) => produto.categoria?.toLowerCase() === categoriaFinal,
      );

      const labelFinal =
        categoria === "outros"
          ? novaCategoria.trim().charAt(0).toUpperCase() +
          novaCategoria.trim().slice(1)
          : categoriaExistente?.categoriaLabel ||
          (CATEGORIAS_BASE.includes(categoriaFinal)
            ? categoriaFinal.charAt(0).toUpperCase() + categoriaFinal.slice(1)
            : categoriaFinal.charAt(0).toUpperCase() +
            categoriaFinal.slice(1));

      const precoAtual = Number(preco);

      const produtoFinal = {
        nome,
        preco: precoAtual,
        precoOriginal: precoAtual,
        estoque: Number(estoque),
        categoria: categoriaFinal,
        categoriaLabel: labelFinal,
        fotos: fotosFinais,
        descricao,
        informacoesAdicionais: informacoes,
        disponivel: true,
        dataAtualizacao: new Date(),
      };

      if (editandoId) {
        await updateDoc(doc(db, "produtos", editandoId), produtoFinal);
        setEditandoId(null);
      } else {
        await addDoc(collection(db, "produtos"), {
          ...produtoFinal,
          dataCriacao: new Date(),
        });
      }
      limparCampos();
      carregarProdutos();
      toast.success("Operação realizada com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar produto.");
    } finally {
      setCarregando(false);
    }
  };

  const limparCampos = () => {
    setNome("");
    setPreco("");
    setEstoque("");
    setDescricao("");
    setEditandoId(null);
    setFotos([]);
    setPreviewUrls([]);
    setCategoria("esporas");
    setNovaCategoria("");
    setInformacoes([]);
  };

  const prepararEdicao = (p) => {
    setAbaAtiva("produtos");
    setEditandoId(p.id);
    setNome(p.nome);
    setEstoque(p.estoque || "");
    setInformacoes(p.informacoesAdicionais || []);
    setPreco(p.preco);
    if (categoriasDisponiveis.includes(p.categoria)) {
      setCategoria(p.categoria);
      setNovaCategoria("");
    } else {
      setCategoria("outros");
      setNovaCategoria(p.categoriaLabel || p.categoria);
    }
    setFotos([]);
    setPreviewUrls(p.fotos || []);
    setDescricao(p.descricao || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmarExclusao = (produto) => {
    setProdutoParaExcluir(produto);
  };

  const cancelarExclusao = () => {
    setProdutoParaExcluir(null);
  };

  const abrirModalDesconto = () => {
    setCategoriaDesconto("");
    setTipoDesconto("percentual");
    setValorDesconto("");
    setProdutosSelecionadosDesconto([]);
    setModalDescontoAberta(true);
  };

  const abrirModalRemocaoDesconto = () => {
    setProdutosSelecionadosRemocaoDesconto([]);
    setModalRemoverDescontoAberta(true);
  };

  const alternarProdutoDesconto = (id) => {
    setProdutosSelecionadosDesconto((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const alternarTodosProdutosVisiveis = () => {
    if (todosProdutosVisiveisSelecionados) {
      setProdutosSelecionadosDesconto((prev) =>
        prev.filter(
          (id) =>
            !produtosFiltradosParaDesconto.some((produto) => produto.id === id),
        ),
      );
      return;
    }

    setProdutosSelecionadosDesconto((prev) => [
      ...new Set([
        ...prev,
        ...produtosFiltradosParaDesconto.map((produto) => produto.id),
      ]),
    ]);
  };

  const confirmarDescontoEmLote = async () => {
    if (!produtosSelecionadosDesconto.length) {
      toast.error("Selecione pelo menos um produto para aplicar o desconto.");
      return;
    }

    const valorNumerico = Number(valorDesconto);

    if (!Number.isFinite(valorNumerico) || valorNumerico < 0) {
      toast.error("Informe um valor válido para o desconto.");
      return;
    }

    if (tipoDesconto === "percentual" && valorNumerico > 100) {
      toast.error("O desconto percentual não pode ser maior que 100%.");
      return;
    }

    setCarregando(true);

    try {
      await Promise.all(
        produtosSelecionadosDesconto.map(async (id) => {
          const produto = produtos.find((item) => item.id === id);
          if (!produto) return;

          const precoOriginalAtual = Number(
            produto.precoOriginal ?? produto.preco ?? 0,
          );
          const precoAtual = Number(produto.preco ?? 0);
          const precoBase =
            precoOriginalAtual > precoAtual ? precoOriginalAtual : precoAtual;

          const novoPreco =
            tipoDesconto === "percentual"
              ? precoBase * (1 - valorNumerico / 100)
              : Math.max(0, precoBase - valorNumerico);

          const precoFinal = Number(Math.max(0, novoPreco).toFixed(2));

          await updateDoc(doc(db, "produtos", id), {
            preco: precoFinal,
            precoOriginal: precoBase,
            dataAtualizacao: new Date(),
          });
        }),
      );

      await carregarProdutos();
      setModalDescontoAberta(false);
      setProdutosSelecionadosDesconto([]);
      setCategoriaDesconto("");
      setValorDesconto("");
      toast.success("Desconto aplicado com sucesso!");
    } catch (error) {
      console.error("Erro ao aplicar desconto em lote:", error);
      toast.error("Não foi possível aplicar o desconto.");
    } finally {
      setCarregando(false);
    }
  };

  const alternarProdutoRemocaoDesconto = (id) => {
    setProdutosSelecionadosRemocaoDesconto((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const confirmarRemocaoDescontoEmLote = async () => {
    if (!produtosSelecionadosRemocaoDesconto.length) {
      toast.error("Selecione pelo menos um produto para remover o desconto.");
      return;
    }

    setCarregando(true);

    try {
      await Promise.all(
        produtosSelecionadosRemocaoDesconto.map(async (id) => {
          const produto = produtos.find((item) => item.id === id);
          if (!produto) return;

          const precoOriginal = Number(
            produto.precoOriginal ?? produto.preco ?? 0,
          );

          await updateDoc(doc(db, "produtos", id), {
            preco: precoOriginal,
            precoOriginal: precoOriginal,
            dataAtualizacao: new Date(),
          });
        }),
      );

      await carregarProdutos();
      setModalRemoverDescontoAberta(false);
      setProdutosSelecionadosRemocaoDesconto([]);
      toast.success("Desconto removido com sucesso!");
    } catch (error) {
      console.error("Erro ao remover desconto em lote:", error);
      toast.error("Não foi possível remover o desconto.");
    } finally {
      setCarregando(false);
    }
  };

  const excluirProduto = async () => {
    if (!produtoParaExcluir) return;

    try {
      await deleteDoc(doc(db, "produtos", produtoParaExcluir.id));
      await carregarProdutos();
      toast.success("Produto excluído com sucesso!");
    } catch (error) {
      console.error("Erro ao excluir produto:", error);
      toast.error("Não foi possível excluir o produto.");
    } finally {
      setProdutoParaExcluir(null);
    }
  };

  const handleUpdateStatus = async (pedido, novoStatus) => {
    if (!pedido || !pedido.id) return;

    if (novoStatus === "shipped") {
      setPedidoParaEnviar(pedido);
      setModalEnvioAberta(true);
      return;
    }

    try {
      const pedidoDoc = doc(db, "pedidos", pedido.id);
      await updateDoc(pedidoDoc, { status: novoStatus });
      toast.success("Status atualizado!");
    } catch (err) {
      console.error("Erro na atualização:", err);
      toast.error("Erro ao atualizar status.");
    }
  };

  const finalizarEnvio = async () => {
    if (!pedidoParaEnviar) return;

    const codigoRastreioFinal = codigoRastreio.trim().toUpperCase();

    // Validação básica para evitar envio sem rastreio quando for Correios
    if (tipoEnvio === "correios" && !codigoRastreioFinal) {
      toast.error("Por favor, insira o código de rastreio.");
      return;
    }

    setCarregando(true);
    try {
      const dadosEnvio = {
        status: "shipped",
        tipoEnvio: tipoEnvio,
        trackingNumber:
          tipoEnvio === "correios" ? codigoRastreioFinal : "ENTREGA EM MÃOS",
        dataEnvio: new Date(),
      };

      // 1. Atualiza o Firestore
      await updateDoc(doc(db, "pedidos", pedidoParaEnviar.id), dadosEnvio);

      const apiUrl =
        process.env.REACT_APP_API_URL || "https://repique-api.onrender.com";

      // 2. Dispara o E-mail para a sua API no Render
      await fetch(`${apiUrl}/notificar-envio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userName: pedidoParaEnviar.userName,
          email: pedidoParaEnviar.email,
          tipoEnvio: tipoEnvio,
          trackingNumber: dadosEnvio.trackingNumber,
        }),
      });

      setModalEnvioAberta(false);
      setCodigoRastreio("");
      toast.success("Pedido enviado e cliente notificado!");
    } catch (err) {
      console.error("Erro ao finalizar envio:", err);
      toast.error("Erro ao processar envio.");
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-left">
          <h2>Painel Admin Repique</h2>
          <nav className="tab-navigation">
            <button
              className={abaAtiva === "pedidos" ? "active" : ""}
              onClick={() => setAbaAtiva("pedidos")}
            >
              <CiBoxes /> Pedidos
            </button>
            <button
              className={abaAtiva === "produtos" ? "active" : ""}
              onClick={() => setAbaAtiva("produtos")}
            >
              <LiaToolsSolid /> Produtos
            </button>
          </nav>
        </div>
        <div className="dashboard-nav">
          <button className="btn-back" onClick={() => navigate("/")}>
            Ver Loja
          </button>
          <button
            className="btn-logout"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            Sair
          </button>
        </div>
      </header>

      {abaAtiva === "produtos" ? (
        <div className="dashboard-grid">
          <section className="admin-form-section">
            <h3>{editandoId ? "📝 Editar Produto" : "Novo Produto"}</h3>
            <form onSubmit={handleSave} className="product-form">
              <input
                placeholder="Nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
              />
              <input
                placeholder="Preço (R$)"
                type="number"
                value={preco}
                onChange={(e) => setPreco(e.target.value)}
                required
              />

              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
              >
                {categoriasDisponiveis.map((categoriaItem) => {
                  const categoriaLabel =
                    categoriaItem === "esporas"
                      ? "Esporas"
                      : categoriaItem === "bombas"
                        ? "Bombas"
                        : categoriaItem === "cuias"
                          ? "Cuias"
                          : produtos.find(
                            (produto) =>
                              produto.categoria?.toLowerCase() ===
                              categoriaItem,
                          )?.categoriaLabel ||
                          categoriaItem.charAt(0).toUpperCase() +
                          categoriaItem.slice(1);

                  return (
                    <option key={categoriaItem} value={categoriaItem}>
                      {categoriaLabel}
                    </option>
                  );
                })}
                <option value="outros">Outros (Criar nova...)</option>
              </select>

              {categoria === "outros" && (
                <input
                  placeholder="Nome da nova categoria"
                  value={novaCategoria}
                  onChange={(e) => setNovaCategoria(e.target.value)}
                  required
                  className="input-nova-categoria"
                />
              )}

              <div className="file-upload-container">
                <label htmlFor="file-input" className="file-upload-label">
                  <FiUploadCloud size={24} />
                  <span>
                    {previewUrls.length > 0
                      ? `${previewUrls.length} foto(s) adicionada(s)`
                      : "Anexar Fotos (máx. 5)"}
                  </span>
                  <span style={{ fontSize: "0.85rem", marginTop: "4px" }}>
                    {previewUrls.length}/{MAX_FOTOS}
                  </span>
                  <input
                    id="file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const novasFotos = [...previewUrls];
                      const novosFotos = [...fotos];

                      for (let file of files) {
                        if (novasFotos.length >= MAX_FOTOS) {
                          toast.error(
                            `Máximo de ${MAX_FOTOS} fotos permitido.`,
                          );
                          break;
                        }
                        novosFotos.push(file);
                        novasFotos.push(URL.createObjectURL(file));
                      }

                      setFotos(novosFotos);
                      setPreviewUrls(novasFotos);
                    }}
                    style={{ display: "none" }}
                  />
                </label>
                {previewUrls.length > 0 && (
                  <div className="upload-preview-grid">
                    {previewUrls.map((preview, index) => (
                      <div key={index} className="upload-preview-item">
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <button
                          type="button"
                          className="btn-remove-foto"
                          onClick={() => {
                            const novasFotos = previewUrls.filter(
                              (_, i) => i !== index,
                            );
                            const novosFotos = fotos.filter(
                              (_, i) => i !== index,
                            );
                            setPreviewUrls(novasFotos);
                            setFotos(novosFotos);
                          }}
                        >
                          ✕
                        </button>
                        <span className="foto-numero">Foto {index + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <textarea
                placeholder="Descrição detalhada"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows="4"
              />

              <input
                placeholder="Quantidade em estoque"
                type="number"
                value={estoque}
                onChange={(e) => setEstoque(e.target.value)}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                required
              />

              <div className="info-adicional-section">
                <div className="section-header-row">
                  <h4>Informações Adicionais</h4>
                  <button
                    type="button"
                    className="btn-add-info"
                    onClick={adicionarInformacao}
                  >
                    + Adicionar Atributo
                  </button>
                </div>

                {informacoes.map((info, index) => (
                  <div key={index} className="info-input-group">
                    <input
                      placeholder="Chave (Ex: Material)"
                      value={info.chave}
                      onChange={(e) =>
                        handleInfoChange(index, "chave", e.target.value)
                      }
                      required
                    />
                    <input
                      placeholder="Valor (Ex: Prata 600)"
                      value={info.valor}
                      onChange={(e) =>
                        handleInfoChange(index, "valor", e.target.value)
                      }
                      required
                    />
                    <button
                      type="button"
                      className="btn-remove-info"
                      onClick={() => removerInformacao(index)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={carregando}
                >
                  {carregando
                    ? "Processando..."
                    : editandoId
                      ? "Salvar Alterações"
                      : "Cadastrar"}
                </button>
                {editandoId && (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={limparCampos}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="admin-list-section">
            <div className="admin-list-header">
              <h3>Catálogo Atual ({produtos.length})</h3>
              <div className="admin-list-actions">
                <button
                  type="button"
                  className="btn-discount-open"
                  onClick={abrirModalDesconto}
                >
                  Aplicar desconto
                </button>
                <button
                  type="button"
                  className="btn-discount-remove"
                  onClick={abrirModalRemocaoDesconto}
                >
                  Remover desconto
                </button>
              </div>
            </div>
            <div className="admin-list">
              {produtos.map((p) => {
                const precoOriginal = Number(p.precoOriginal ?? p.preco ?? 0);
                const precoAtual = Number(p.preco ?? 0);
                const temDesconto = precoOriginal > precoAtual;

                return (
                  <div key={p.id} className="admin-card">
                    <img src={p.fotos[0]} alt={p.nome} />
                    <div className="card-content">
                      <h4>{p.nome}</h4>
                      <div className="card-preco-admin">
                        {temDesconto && (
                          <span className="card-preco-original-admin">
                            R${" "}
                            {precoOriginal.toLocaleString("pt-BR", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        )}
                        <p
                          className={
                            temDesconto ? "card-preco-desconto-admin" : ""
                          }
                        >
                          R${" "}
                          {precoAtual.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                      <p>Estoque: {p.estoque}</p>
                      <small className="card-category-tag">
                        {p.categoriaLabel}
                      </small>
                    </div>
                    <div className="card-btns">
                      <button
                        className="edit"
                        onClick={() => prepararEdicao(p)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="del"
                        onClick={() => confirmarExclusao(p)}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {produtoParaExcluir && (
            <div className="modal-overlay" onClick={cancelarExclusao}>
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Confirmar exclusão</h3>
                  <button
                    type="button"
                    className="close-modal"
                    onClick={cancelarExclusao}
                    aria-label="Fechar"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-section">
                  <p>
                    Tem certeza que deseja excluir o produto{" "}
                    <strong>{produtoParaExcluir.nome}</strong>?
                  </p>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={excluirProduto}
                  >
                    Sim, excluir
                  </button>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={cancelarExclusao}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <section className="admin-orders-section">
          <h3>Gestão de Pedidos ({pedidos.length})</h3>

          <div className="filters-container">
            <div className="search-input-wrapper">
              <IoSearchSharp className="search-icon" />
              <input
                type="text"
                placeholder="Pesquisar por cliente ou código..."
                value={filtroPesquisa}
                onChange={(e) => setFiltroPesquisa(e.target.value)}
                className="filter-input"
              />
            </div>
            <input
              type="date"
              value={filtroData}
              onChange={(e) => setFiltroData(e.target.value)}
              className="filter-input filter-date"
            />
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="filter-input filter-status"
            >
              <option value="">Todos os Status</option>
              <option value="pending">Pendente</option>
              <option value="approved">Aprovado</option>
              <option value="shipped">Enviado</option>
              <option value="delivered">Entregue</option>
              <option value="cancelled">Cancelado</option>
            </select>
            {(filtroPesquisa || filtroData || filtroStatus) && (
              <button
                className="btn-clear-filters"
                onClick={() => {
                  setFiltroPesquisa("");
                  setFiltroData("");
                  setFiltroStatus("");
                }}
              >
                Limpar Filtros
              </button>
            )}
          </div>

          <div className="orders-table-wrapper">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Cliente</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos
                  .filter((pedido) => {
                    const matchPesquisa =
                      !filtroPesquisa ||
                      pedido.userName
                        ?.toLowerCase()
                        .includes(filtroPesquisa.toLowerCase()) ||
                      pedido.id
                        .slice(-6)
                        .toUpperCase()
                        .includes(filtroPesquisa.toUpperCase());
                    const matchData =
                      !filtroData ||
                      pedido.dataCriacao
                        ?.toDate()
                        .toLocaleDateString("pt-BR") ===
                      new Date(filtroData).toLocaleDateString("pt-BR");
                    const matchStatus =
                      !filtroStatus || pedido.status === filtroStatus;
                    return matchPesquisa && matchData && matchStatus;
                  })
                  .map((pedido) => (
                    <tr key={pedido.id}>
                      <td data-label="Data">
                        <div>
                          {pedido.dataCriacao
                            ?.toDate()
                            .toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                        </div>
                        <small
                          style={{
                            color: "var(--text-secondary)",
                            fontSize: "0.75rem",
                            marginTop: "4px",
                            display: "block",
                          }}
                        >
                          #{pedido.id.slice(-6).toUpperCase()}
                        </small>
                      </td>
                      <td data-label="Cliente">{pedido.userName}</td>
                      <td data-label="Total">R$ {pedido.total?.toFixed(2)}</td>
                      <td data-label="Status">
                        <span className={`status-badge-admin ${pedido.status}`}>
                          {getStatusEmPortugues(pedido.status)}
                        </span>
                      </td>
                      <td className="actions-cell" data-label="Ações">
                        <button
                          className="btn-details"
                          onClick={() => setPedidoSelecionado(pedido)}
                        >
                          VER DETALHES
                        </button>
                        <select
                          value={pedido.status}
                          onChange={(e) =>
                            handleUpdateStatus(pedido, e.target.value)
                          }
                          className="status-select"
                        >
                          <option value="pending">Pendente</option>
                          <option value="approved">Aprovado</option>
                          <option value="shipped">Enviado</option>
                          <option value="delivered">Entregue</option>
                          <option value="cancelled">Cancelado</option>
                        </select>
                        {pedido.status === "shipped" &&
                          pedido.trackingNumber && (
                            <div className="tracking-info-display">
                              <small>CÓDIGO DE RASTREIO:</small>
                              <small>
                                {" "}
                                {pedido.trackingNumber?.toUpperCase()}
                              </small>
                            </div>
                          )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* --- MODAL DE DETALHES --- */}
      {modalDescontoAberta && (
        <div
          className="modal-overlay"
          onClick={() => setModalDescontoAberta(false)}
        >
          <div
            className="modal-content modal-desconto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Aplicar desconto em lote</h3>
              <button
                type="button"
                className="close-modal"
                onClick={() => setModalDescontoAberta(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            <div className="modal-section">
              <h4>Tipo de desconto</h4>
              <div className="discount-type-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="tipoDesconto"
                    value="percentual"
                    checked={tipoDesconto === "percentual"}
                    onChange={(e) => setTipoDesconto(e.target.value)}
                  />
                  <span>Percentual (%)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="tipoDesconto"
                    value="fixo"
                    checked={tipoDesconto === "fixo"}
                    onChange={(e) => setTipoDesconto(e.target.value)}
                  />
                  <span>Valor fixo (R$)</span>
                </label>
              </div>
            </div>

            <div className="modal-section">
              <h4>Valor do desconto</h4>
              <input
                type="number"
                min="0"
                step={tipoDesconto === "percentual" ? "0.01" : "1"}
                value={valorDesconto}
                onChange={(e) => setValorDesconto(e.target.value)}
                placeholder={
                  tipoDesconto === "percentual" ? "Ex: 10" : "Ex: 25"
                }
              />
            </div>

            <div className="modal-section">
              <h4>Selecionar por categoria</h4>
              <div className="discount-category-row">
                <select
                  value={categoriaDesconto}
                  onChange={(e) => setCategoriaDesconto(e.target.value)}
                >
                  <option value="">Todos</option>
                  {categoriasParaDesconto.map((categoria) => (
                    <option key={categoria} value={categoria}>
                      {categoria}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-section">
              <div className="discount-selection-header">
                <h4>Produtos selecionados</h4>
                <label className="select-all-checkbox">
                  <input
                    type="checkbox"
                    checked={todosProdutosVisiveisSelecionados}
                    onChange={alternarTodosProdutosVisiveis}
                  />
                  <span>Selecionar todos</span>
                </label>
              </div>

              <div className="discount-list">
                {produtosFiltradosParaDesconto.length === 0 ? (
                  <span className="no-products-filtered">
                    Nenhum produto encontrado para esta categoria.
                  </span>
                ) : (
                  produtosFiltradosParaDesconto.map((produto) => (
                    <label key={produto.id} className="discount-product-option">
                      <input
                        type="checkbox"
                        checked={produtosSelecionadosDesconto.includes(
                          produto.id,
                        )}
                        onChange={() => alternarProdutoDesconto(produto.id)}
                      />
                      <span>{produto.nome}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={confirmarDescontoEmLote}
                disabled={carregando}
              >
                {carregando ? "Aplicando..." : "Confirmar"}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setModalDescontoAberta(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {modalRemoverDescontoAberta && (
        <div
          className="modal-overlay"
          onClick={() => setModalRemoverDescontoAberta(false)}
        >
          <div
            className="modal-content modal-desconto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Remover desconto aplicado</h3>
              <button
                type="button"
                className="close-modal"
                onClick={() => setModalRemoverDescontoAberta(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            <div className="modal-section">
              <h4>Itens com desconto</h4>
              {produtosComDesconto.length === 0 ? (
                <p>Nenhum produto com desconto aplicado no momento.</p>
              ) : (
                <div className="discount-list">
                  {produtosComDesconto.map((produto) => (
                    <label key={produto.id} className="discount-product-option">
                      <input
                        type="checkbox"
                        checked={produtosSelecionadosRemocaoDesconto.includes(
                          produto.id,
                        )}
                        onChange={() =>
                          alternarProdutoRemocaoDesconto(produto.id)
                        }
                      />
                      <span>{produto.nome}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={confirmarRemocaoDescontoEmLote}
                disabled={carregando || produtosComDesconto.length === 0}
              >
                {carregando ? "Removendo..." : "Confirmar"}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setModalRemoverDescontoAberta(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {pedidoSelecionado && (
        <div
          className="modal-overlay"
          onClick={() => setPedidoSelecionado(null)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h3>Pedido #{pedidoSelecionado.id.slice(-6).toUpperCase()}</h3>
              <button
                className="close-modal"
                onClick={() => setPedidoSelecionado(null)}
              >
                &times;
              </button>
            </header>
            <div className="modal-body">
              <div className="modal-section">
                <h4>
                  <IoLocationOutline /> Entrega
                </h4>
                <p>{pedidoSelecionado.endereco?.complemento}</p>
                <p>
                  {pedidoSelecionado.endereco?.endereco},{" "}
                  {pedidoSelecionado.endereco?.numero}
                </p>
                <p>
                  {pedidoSelecionado.endereco?.cidade} -{" "}
                  {pedidoSelecionado.endereco?.estado}
                </p>
                <p>CEP: {pedidoSelecionado.endereco?.cep}</p>
              </div>
              <div className="modal-section">
                <h4>
                  <CgProfile /> Cliente
                </h4>
                <p>
                  {pedidoSelecionado.userName} ({pedidoSelecionado.email})
                </p>
                <p>{pedidoSelecionado.endereco?.telefone}</p>
              </div>
              <div className="modal-section">
                <h4>
                  <CiBoxes /> Itens
                </h4>
                {pedidoSelecionado.itens?.map((it, i) => (
                  <div key={i} className="modal-item">
                    <span>
                      {it.quantity}x {it.title}
                    </span>
                    <span>
                      {(it.unit_price * it.quantity).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                ))}
                <div className="modal-total">
                  <span>TOTAL:</span>
                  <span>
                    {pedidoSelecionado.total?.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>
                </div>
              </div>
              <div className="modal-section">
                <h4>
                  <CiCreditCard1 /> Pagamento
                </h4>
                <p>
                  Método: {pedidoSelecionado.paymentMethod} | ID:{" "}
                  {pedidoSelecionado.paymentId}
                </p>
              </div>
            </div>
            <footer className="modal-footer">
              <button className="btn-print" onClick={() => window.print()}>
                IMPRIMIR ETIQUETA
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* --- MODAL DE CONFIGURAÇÃO DE ENVIO --- */}
      {modalEnvioAberta && (
        <div className="modal-overlay">
          <div className="modal-content envio-modal">
            <header className="modal-header">
              <h3>Configurar Envio</h3>
              <button
                className="close-modal"
                onClick={() => setModalEnvioAberta(false)}
              >
                &times;
              </button>
            </header>
            <div className="modal-body">
              <p>
                Como você entregará o pedido de{" "}
                <strong>{pedidoParaEnviar?.userName}</strong>?
              </p>
              <div className="envio-options">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="tipoEnvio"
                    value="pessoal"
                    checked={tipoEnvio === "pessoal"}
                    onChange={() => setTipoEnvio("pessoal")}
                  />
                  <span>Entrega Pessoalmente</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="tipoEnvio"
                    value="correios"
                    checked={tipoEnvio === "correios"}
                    onChange={() => setTipoEnvio("correios")}
                  />
                  <span>Envio via Correios</span>
                </label>
              </div>

              {tipoEnvio === "correios" && (
                <div className="input-group">
                  <label>CÓDIGO DE RASTREIO</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: BR123456789"
                    value={codigoRastreio}
                    onChange={(e) =>
                      setCodigoRastreio(e.target.value.toUpperCase())
                    }
                    className="input-rastreio"
                  />
                </div>
              )}
            </div>
            <footer className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setModalEnvioAberta(false)}
              >
                CANCELAR
              </button>
              <button
                className="btn-confirm"
                onClick={finalizarEnvio}
                disabled={carregando}
              >
                {carregando ? "SALVANDO..." : "CONFIRMAR ENVIO"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
